/**
 * Word Exporter v3
 *
 * Handles exporting report data to Word format
 */

import { formatReportData } from './reportRenderer';
import { extractChartConfig } from './chartRenderer';
import { Chart } from 'chart.js/auto';

function stripHtmlTags(value: string): string {
  return value.replace(/<[^>]*>/g, '');
}

export class WordExporter {
  /**
   * Capture all rendered chart images from the page DOM.
   */
  private captureChartImages(): string[] {
    const images: string[] = [];
    const canvases = document.querySelectorAll('canvas');
    console.log(`[WORD_EXPORT v3] Found ${canvases.length} canvas elements on page`);
    canvases.forEach((canvas, i) => {
      try {
        const htmlCanvas = canvas as HTMLCanvasElement;
        console.log(`[WORD_EXPORT v3] Canvas #${i}: ${htmlCanvas.width}x${htmlCanvas.height}, id="${htmlCanvas.id}"`);
        if (htmlCanvas.width > 10 && htmlCanvas.height > 10) {
          const dataUrl = htmlCanvas.toDataURL('image/png');
          if (dataUrl && dataUrl.length > 500) {
            images.push(dataUrl);
            console.log(`[WORD_EXPORT v3] Canvas #${i} captured OK (${Math.round(dataUrl.length / 1024)}KB)`);
          } else {
            console.warn(`[WORD_EXPORT v3] Canvas #${i} skipped - blank (${dataUrl?.length} chars)`);
          }
        }
      } catch (e) {
        console.warn(`[WORD_EXPORT v3] Canvas #${i} capture error:`, e);
      }
    });
    console.log(`[WORD_EXPORT v3] Total captured: ${images.length} chart images from DOM`);
    return images;
  }

  /**
   * Render a chart from chart_html config to a PNG data URL.
   * Used as fallback when DOM capture doesn't have enough images.
   */
  private renderChartToImage(chartHtml: string): string | null {
    try {
      console.log('[WORD_EXPORT v3] renderChartToImage: extracting config from chart_html...');
      const config = extractChartConfig(chartHtml);
      if (!config) {
        console.warn('[WORD_EXPORT v3] renderChartToImage: extractChartConfig returned null');
        console.warn('[WORD_EXPORT v3] chart_html preview:', chartHtml.substring(0, 200));
        return null;
      }
      console.log(`[WORD_EXPORT v3] renderChartToImage: config OK - type=${config.type}, labels=${config.labels?.length}, datasets=${config.datasets?.length}`);

      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 400;
      canvas.style.position = 'fixed';
      canvas.style.left = '-9999px';
      canvas.style.top = '-9999px';
      document.body.appendChild(canvas);

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('[WORD_EXPORT v3] renderChartToImage: could not get 2d context');
        document.body.removeChild(canvas);
        return null;
      }

      const chartType = config.type || 'bar';
      const chart = new Chart(ctx, {
        type: chartType,
        data: {
          labels: config.labels || [],
          datasets: config.datasets || []
        },
        options: {
          ...(config.options || {}),
          animation: false,
          responsive: false,
          plugins: {
            ...(config.options?.plugins || {}),
            legend: {
              ...(config.options?.plugins?.legend || {}),
              position: ['pie', 'doughnut', 'polarArea'].includes(chartType) ? 'right' as const : 'top' as const
            }
          }
        }
      });

      const dataUrl = canvas.toDataURL('image/png');
      console.log(`[WORD_EXPORT v3] renderChartToImage: rendered OK (${Math.round(dataUrl.length / 1024)}KB)`);

      chart.destroy();
      document.body.removeChild(canvas);

      if (dataUrl && dataUrl.length > 500) {
        return dataUrl;
      }
      console.warn('[WORD_EXPORT v3] renderChartToImage: rendered image appears blank');
      return null;
    } catch (e) {
      console.error('[WORD_EXPORT v3] renderChartToImage error:', e);
      return null;
    }
  }

  /**
   * Get chart image: try DOM capture first, fall back to re-rendering
   */
  private getChartImage(chartImages: string[], chartIndex: number, chartHtml: string): string | null {
    if (chartIndex < chartImages.length) {
      console.log(`[WORD_EXPORT v3] getChartImage #${chartIndex}: using DOM capture`);
      return chartImages[chartIndex];
    }
    console.log(`[WORD_EXPORT v3] getChartImage #${chartIndex}: no DOM image (have ${chartImages.length}), re-rendering...`);
    return this.renderChartToImage(chartHtml);
  }

  /**
   * Create an <img> element from a chart data URL
   */
  private createChartImg(dataUrl: string, description: string): HTMLElement {
    const img = document.createElement('img');
    img.src = dataUrl;
    img.style.width = '90%';
    img.style.maxWidth = '500px';
    img.style.display = 'block';
    img.style.margin = '10px auto';
    img.alt = description || 'Chart';
    return img;
  }

  /**
   * Generate a clean filename from the report title
   */
  private generateFilename(title: string): string {
    if (!title) return 'report.doc';
    // Use Unicode property escapes: \p{L} = any letter (Cyrillic, Latin, etc.), \p{N} = any digit
    const clean = title
      .replace(/[^\p{L}\p{N}\s\-]/gu, '')
      .trim()
      .replace(/\s+/g, '_');
    console.log(`[WORD_EXPORT v3] generateFilename: "${title}" -> "${clean || 'report'}.doc"`);
    return `${clean || 'report'}.doc`;
  }

  /**
   * Exports report data to Word format
   */
  async exportToWord(reportData: any): Promise<void> {
    console.log('[WORD_EXPORT v3] === Starting Word export ===');
    if (!reportData) {
      console.error('[WORD_EXPORT v3] No report data provided');
      return;
    }

    try {
      // Capture chart images from rendered page
      const chartImages = this.captureChartImages();
      let chartIndex = 0;

      // Format report data
      const formattedData = formatReportData(reportData);
      console.log('[WORD_EXPORT v3] Formatted data:', formattedData);

      // Build document
      const doc = document.createElement('div');
      doc.className = 'word-export';

      // Title
      const title = document.createElement('h1');
      title.textContent = formattedData.summary?.report_title || 'Отчет';
      doc.appendChild(title);

      // Summary
      if (formattedData.summary?.summary) {
        const summary = document.createElement('div');
        summary.className = 'summary';
        summary.innerHTML = `<h2>Краткое содержание</h2><p>${formattedData.summary.summary}</p>`;
        doc.appendChild(summary);
      }

      // Key findings
      if (formattedData.summary?.key_findings?.length > 0) {
        const findings = document.createElement('div');
        findings.className = 'findings';
        findings.innerHTML = '<h2>Ключевые выводы</h2><ul>' +
          formattedData.summary.key_findings.map((f: string) => `<li>${f}</li>`).join('') +
          '</ul>';
        doc.appendChild(findings);
      }

      // Recommendations
      if (formattedData.summary?.recommendations?.length > 0) {
        const recommendations = document.createElement('div');
        recommendations.className = 'recommendations';
        recommendations.innerHTML = '<h2>Рекомендации</h2><ul>' +
          formattedData.summary.recommendations.map((r: string) => `<li>${r}</li>`).join('') +
          '</ul>';
        doc.appendChild(recommendations);
      }

      // Report sections
      if (formattedData.reports?.length > 0) {
        formattedData.reports.forEach((report: any, idx: number) => {
          const reportSection = document.createElement('div');
          reportSection.className = 'report-section';

          const sectionTitle = document.createElement('h2');
          sectionTitle.textContent = report.name || `Раздел ${idx + 1}`;
          reportSection.appendChild(sectionTitle);

          const hasBlocks = report.actions?.blocks?.length > 0;

          if (hasBlocks) {
            console.log(`[WORD_EXPORT v3] Block format: "${report.name}" (${report.actions.blocks.length} blocks)`);
            report.actions.blocks.forEach((block: any, blockIdx: number) => {
              if (block.type === 'heading') {
                // Skip the first heading if it duplicates the report name (already rendered as section title)
                if (blockIdx === 0 && block.text && block.text === report.name) {
                  console.log(`[WORD_EXPORT v3] Skipping duplicate heading: "${block.text}"`);
                  return;
                }
                const heading = document.createElement(`h${block.level || 3}`);
                heading.textContent = block.text || '';
                reportSection.appendChild(heading);
              } else if (block.type === 'text') {
                const textDiv = document.createElement('div');
                textDiv.className = 'text-content';
                textDiv.innerHTML = block.content || '';
                reportSection.appendChild(textDiv);
              } else if (block.type === 'visualization') {
                const vizDiv = document.createElement('div');
                vizDiv.className = 'visualization';

                if (block.description) {
                  const descDiv = document.createElement('h3');
                  descDiv.textContent = block.description;
                  vizDiv.appendChild(descDiv);
                }

                if (block.table_data) {
                  vizDiv.appendChild(this.createTable(block.table_data));
                }

                if (block.chart_html) {
                  console.log(`[WORD_EXPORT v3] Chart #${chartIndex}: "${block.description || 'no desc'}"`);
                  const imgData = this.getChartImage(chartImages, chartIndex, block.chart_html);
                  chartIndex++;
                  if (imgData) {
                    console.log(`[WORD_EXPORT v3] Chart #${chartIndex - 1}: SUCCESS (${Math.round(imgData.length / 1024)}KB)`);
                    vizDiv.appendChild(this.createChartImg(imgData, block.description || ''));
                  } else {
                    console.error(`[WORD_EXPORT v3] Chart #${chartIndex - 1}: FAILED - no image data`);
                  }
                }

                reportSection.appendChild(vizDiv);
              }
            });
          } else {
            console.log(`[WORD_EXPORT v3] Legacy format: "${report.name}"`);

            if (report.actions?.analytical_note) {
              const noteDiv = document.createElement('div');
              noteDiv.className = 'analytical-note';
              noteDiv.innerHTML = `<p>${report.actions.analytical_note}</p>`;
              reportSection.appendChild(noteDiv);
            }

            if (report.actions?.visualizations?.length > 0) {
              report.actions.visualizations.forEach((viz: any) => {
                const vizDiv = document.createElement('div');
                vizDiv.className = 'visualization';

                if (viz.description) {
                  const descDiv = document.createElement('h3');
                  descDiv.textContent = viz.description;
                  vizDiv.appendChild(descDiv);
                }

                if (viz.table_data) {
                  vizDiv.appendChild(this.createTable(viz.table_data));
                }

                if (viz.chart_html) {
                  console.log(`[WORD_EXPORT v3] Chart #${chartIndex}: "${viz.description || 'no desc'}"`);
                  const imgData = this.getChartImage(chartImages, chartIndex, viz.chart_html);
                  chartIndex++;
                  if (imgData) {
                    console.log(`[WORD_EXPORT v3] Chart #${chartIndex - 1}: SUCCESS (${Math.round(imgData.length / 1024)}KB)`);
                    vizDiv.appendChild(this.createChartImg(imgData, viz.description || ''));
                  } else {
                    console.error(`[WORD_EXPORT v3] Chart #${chartIndex - 1}: FAILED - no image data`);
                  }
                }

                reportSection.appendChild(vizDiv);
              });
            }
          }

          doc.appendChild(reportSection);
        });
      }

      // Generate filename
      const filename = this.generateFilename(title.textContent || '');

      // Build Word-compatible HTML
      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <title>${title.textContent}</title>
          <style>
            body { font-family: 'Calibri', sans-serif; margin: 1cm; }
            h1 { font-size: 18pt; color: #2F5496; }
            h2 { font-size: 16pt; color: #2F5496; }
            h3 { font-size: 14pt; color: #1F3864; }
            p { font-size: 12pt; }
            ul { margin-left: 20px; }
            li { font-size: 12pt; }
            img { max-width: 450px; height: auto; display: block; margin: 10px auto; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
            th { background-color: #f2f2f2; padding: 8px; border: 1px solid #ddd; text-align: left; }
            td { padding: 8px; border: 1px solid #ddd; }
            .visualization { page-break-inside: avoid; }
          </style>
        </head>
        <body>
          ${doc.innerHTML}
        </body>
        </html>
      `;

      // Download as .doc
      const blob = new Blob([html], { type: 'application/msword' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`[WORD_EXPORT v3] === Export complete: ${chartIndex} charts processed, file: ${filename} ===`);

    } catch (error) {
      console.error('[WORD_EXPORT v3] Export error:', error);
      throw error;
    }
  }

  /**
   * Create a styled table element from table_data
   */
  private createTable(tableData: any): HTMLElement {
    const tableDiv = document.createElement('div');
    tableDiv.className = 'table-container';

    const table = document.createElement('table');
    table.className = 'data-table';
    table.border = "1";
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";

    if (tableData.headers && Array.isArray(tableData.headers)) {
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');

      tableData.headers.forEach((header: any) => {
        const th = document.createElement('th');
        th.textContent = typeof header === 'object' && header !== null ? (header.value ?? '') : String(header ?? '');
        th.style.padding = "8px";
        th.style.backgroundColor = "#f2f2f2";
        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);
    }

    if (tableData.rows && Array.isArray(tableData.rows)) {
      const tbody = document.createElement('tbody');

      tableData.rows.forEach((row: any) => {
        const tr = document.createElement('tr');

        const cells = Array.isArray(row) ? row : (row.values && Array.isArray(row.values)) ? row.values : Object.values(row);

        cells.forEach((cell: any) => {
          const td = document.createElement('td');
          td.style.padding = "8px";
          td.style.borderBottom = "1px solid #ddd";

          if (typeof cell === 'object' && cell !== null) {
            td.textContent = stripHtmlTags(cell.value || '');
            if (cell.classes) td.className = cell.classes;
          } else {
            td.textContent = stripHtmlTags(String(cell));
          }

          tr.appendChild(td);
        });

        tbody.appendChild(tr);
      });

      table.appendChild(tbody);
    }

    tableDiv.appendChild(table);
    return tableDiv;
  }
}

export default new WordExporter();
